import { TableCell, TextField as TextFieldRoot } from '@mui/material'
import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'

import { FlexBox } from 'components/Box'
import { CodeField, NumberField, Select, Switch, TextField } from 'components/Form'
import {
  AbTestsAddEditContext,
  AbTestsAddEditContextType,
} from 'features/ab-tests/contexts/AbTestsAddEditContext'
import { inputWidth } from 'providers/material-ui/theme/constants'
import { AbTestsCommonSchema } from '../../abTestsCommonSchema'
import { TABLE_BORDER } from './constants'
import { useDisableDependentInput } from './hooks/useDisableDependentInput'
import { useHideInput } from './hooks/useHideInput'
import { RowDataVariant, ValueColumnCellsProps } from './types'

export const ValueColumnCells = ({ variants }: ValueColumnCellsProps): JSX.Element => {
  const { isDisabled, loading: formLoading } =
    useContext<AbTestsAddEditContextType>(AbTestsAddEditContext)

  const { control } = useFormContext<AbTestsCommonSchema>()
  const { hideInput } = useHideInput()
  const { isDisabledBy } = useDisableDependentInput()

  const ReadOnlyField = ({
    prefix,
    type,
    workspaceValue,
  }: Pick<RowDataVariant, 'prefix' | 'type' | 'workspaceValue'>): JSX.Element => {
    const calculatedWidth = ((type: RowDataVariant['type']): number | string => {
      switch (type) {
        case 'float':
        case 'floatPercent':
        case 'integer': {
          return inputWidth.sm
        }
        default: {
          return inputWidth.md
        }
      }
    })(type)

    return (
      <TextFieldRoot
        disabled={isDisabled || isDisabledBy(prefix) || formLoading}
        sx={{
          minWidth: calculatedWidth,
          width: calculatedWidth,
        }}
        type={type}
        value={workspaceValue}
      />
    )
  }

  const input = ({
    options,
    prefix,
    type,
  }: Pick<RowDataVariant, 'options' | 'prefix' | 'type'>): JSX.Element => {
    const name = `variants.${prefix}`

    switch (type) {
      case 'boolean': {
        return (
          <Switch
            control={control}
            disabled={isDisabled || formLoading}
            name={name}
          />
        )
      }
      case 'enum': {
        return (
          <Select
            control={control}
            disabled={isDisabled || formLoading}
            name={name}
            options={options ?? []}
          />
        )
      }
      case 'float':
      case 'floatPercent':
      case 'integer': {
        return (
          <NumberField
            control={control}
            disabled={isDisabled || formLoading}
            name={name}
          />
        )
      }

      case 'json': {
        return (
          <CodeField
            control={control}
            disabled={isDisabled || formLoading}
            language='javascript'
            name={name}
          />
        )
      }
      case 'stringCode': {
        return (
          <CodeField
            control={control}
            disabled={isDisabled || formLoading}
            language='javascript'
            name={name}
          />
        )
      }
      default: {
        return (
          <TextField
            control={control}
            disabled={isDisabled || formLoading}
            name={name}
          />
        )
      }
    }
  }

  return (
    <>
      {Object.values(variants).map(
        ({ options, prefix, type, workspaceValue }: RowDataVariant): JSX.Element => (
          <TableCell
            align='center'
            key={prefix}
            sx={{
              border: TABLE_BORDER,
            }}
          >
            <FlexBox axis='xy'>
              {hideInput(prefix) ? (
                <></>
              ) : isDisabledBy(prefix) ? (
                <ReadOnlyField
                  prefix={prefix}
                  type={type}
                  workspaceValue={workspaceValue}
                />
              ) : (
                input({ options, prefix, type })
              )}
            </FlexBox>
          </TableCell>
        ),
      )}
    </>
  )
}
